import { Session, StockSymbol } from '@benzinga/session';
import { ScannerProtos } from './scanner_protos';

export type RelatedSymbol = {
  change?: number;
  changePercent?: number;
  close?: number;
  dividend?: number;
  dividendYield?: number;
  exchange?: string;
  forwardPERatio?: number;
  marketCap: number;
  name: string;
  pe?: number;
  sector?: string;
  shareFloat?: number;
  sharesOutstanding?: number;
  symbol: StockSymbol;
};

export type WatchlistRelatedSymbol = {
  marketCap: number;
  name: string;
  sector?: string;
  symbol: string;
};

export const RELATED_SYMBOL_RESULT_LIMIT = 20;
export const RELATED_SYMBOL_SEED_LIMIT = 12;
export const RELATED_SYMBOL_SECTOR_LIMIT = 4;
export const RELATED_SYMBOL_EXCLUSION_BUFFER_CAP = 50;

export const getRelatedSymbolsFetchLimit = (sectorCount: number, watchlistSymbolCount: number): number => {
  if (sectorCount <= 0) return 0;

  const peerLimitPerSector = Math.ceil(RELATED_SYMBOL_RESULT_LIMIT / sectorCount) + 5;
  const exclusionBuffer = Math.min(watchlistSymbolCount, RELATED_SYMBOL_EXCLUSION_BUFFER_CAP);

  return sectorCount * peerLimitPerSector + RELATED_SYMBOL_RESULT_LIMIT + exclusionBuffer;
};

const DEFAULT_LOOKUP_FIELDS = ['symbol', 'gicsSectorName', 'gicsIndustryName', 'gicsSubIndustryName'];
const DEFAULT_SCANNER_FIELDS = ['symbol', 'name', 'gicsSectorName', 'marketCap'];

export type RelatedSymbolsForSymbolOptions = {
  excludeSymbols?: string[];
  fields?: string[];
  limit?: number;
  symbol: StockSymbol;
  useIndustry?: boolean;
};

export type RelatedSymbolsForSymbolsOptions = {
  fields?: string[];
  limit?: number;
  sectorLimit?: number;
  seedLimit?: number;
  symbols: string[];
};

export const toMarketCapNumber = (value: unknown): number => {
  if (value == null) return 0;
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'object' && 'toNumber' in value) {
    const longValue = value as { toNumber: () => number };
    if (typeof longValue.toNumber === 'function') {
      const converted = longValue.toNumber();
      return Number.isFinite(converted) ? converted : 0;
    }
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const sampleSymbolsEvenly = (symbols: string[], limit: number): string[] => {
  if (symbols.length <= limit) return symbols;
  if (limit <= 0) return [];

  const sampled: string[] = [];
  const seen = new Set<string>();
  for (let index = 0; index < limit; index += 1) {
    const symbol = symbols[Math.floor((index * symbols.length) / limit)];
    if (!symbol || seen.has(symbol)) continue;
    seen.add(symbol);
    sampled.push(symbol);
  }
  return sampled;
};

export const pickTopSectors = (instruments: Array<{ gicsSectorName?: string | null }>, limit: number): string[] => {
  const counts = new Map<string, number>();
  for (const instrument of instruments) {
    const sector = instrument.gicsSectorName?.trim();
    if (!sector) continue;
    counts.set(sector, (counts.get(sector) ?? 0) + 1);
  }

  return [...counts.entries()]
    .sort((left, right) => right[1] - left[1] || left[0].localeCompare(right[0]))
    .slice(0, limit)
    .map(([sector]) => sector);
};

export const allocateSectorSlots = (total: number, sectorCount: number): number[] => {
  if (total <= 0 || sectorCount <= 0) return [];
  const base = Math.floor(total / sectorCount);
  const remainder = total % sectorCount;
  return Array.from({ length: sectorCount }, (_, index) => base + (index < remainder ? 1 : 0));
};

const byMarketCapDesc = (
  left: WatchlistRelatedSymbol | RelatedSymbol,
  right: WatchlistRelatedSymbol | RelatedSymbol,
): number => right.marketCap - left.marketCap || left.symbol.localeCompare(right.symbol);

export const mergeRelatedSymbols = <T extends WatchlistRelatedSymbol>(
  instruments: Array<{
    symbol?: string | null;
    name?: string | null;
    gicsSectorName?: string | null;
    marketCap?: unknown;
  }>,
  excludedSymbols: Set<string>,
  limit: number,
  sectorOrder: string[] = [],
  mapInstrument: (instrument: {
    symbol?: string | null;
    name?: string | null;
    gicsSectorName?: string | null;
    marketCap?: unknown;
  }) => T,
): T[] => {
  const peersBySymbol = new Map<string, T>();

  for (const instrument of instruments) {
    const symbol = instrument.symbol?.trim().toUpperCase();
    if (!symbol || excludedSymbols.has(symbol) || peersBySymbol.has(symbol)) continue;

    peersBySymbol.set(symbol, mapInstrument(instrument));
  }

  const peers = [...peersBySymbol.values()].sort(byMarketCapDesc);
  if (peers.length === 0 || limit <= 0) return [];
  if (sectorOrder.length <= 1) return peers.slice(0, limit);

  const slots = allocateSectorSlots(limit, sectorOrder.length);
  const buckets = sectorOrder.map((sector, index) => {
    const take = slots[index] ?? 0;
    return peers.filter(peer => peer.sector === sector).slice(0, take);
  });

  const selected: T[] = [];
  const selectedSymbols = new Set<string>();
  let round = 0;
  let addedInRound = true;

  while (selected.length < limit && addedInRound) {
    addedInRound = false;
    for (const bucket of buckets) {
      const peer = bucket[round];
      if (!peer || selectedSymbols.has(peer.symbol)) continue;
      selected.push(peer);
      selectedSymbols.add(peer.symbol);
      addedInRound = true;
      if (selected.length >= limit) break;
    }
    round += 1;
  }

  if (selected.length < limit) {
    for (const peer of peers) {
      if (selected.length >= limit) break;
      if (selectedSymbols.has(peer.symbol)) continue;
      if (peer.sector && !sectorOrder.includes(peer.sector)) continue;
      selected.push(peer);
      selectedSymbols.add(peer.symbol);
    }
  }

  return selected;
};

export const mapInstrumentToWatchlistRelatedSymbol = (instrument: {
  symbol?: string | null;
  name?: string | null;
  gicsSectorName?: string | null;
  marketCap?: unknown;
}): WatchlistRelatedSymbol => {
  const symbol = instrument.symbol?.trim().toUpperCase() ?? '';
  return {
    marketCap: toMarketCapNumber(instrument.marketCap),
    name: instrument.name?.trim() || symbol,
    sector: instrument.gicsSectorName?.trim() || undefined,
    symbol,
  };
};

export const mapInstrumentToRelatedSymbol = (instrument: {
  change?: number | null;
  changePercent?: number | null;
  close?: number | null;
  dividend?: number | null;
  dividendYield?: number | null;
  exchange?: string | null;
  forwardPERatio?: number | null;
  gicsSectorName?: string | null;
  marketCap?: unknown;
  name?: string | null;
  priceToEarnings?: number | null;
  shareFloat?: number | null;
  sharesOutstanding?: number | null;
  symbol?: string | null;
}): RelatedSymbol => {
  const symbol = instrument.symbol?.trim().toUpperCase() ?? '';
  return {
    change: instrument.change ?? undefined,
    changePercent: instrument.changePercent ?? undefined,
    close: instrument.close ?? undefined,
    dividend: instrument.dividend ?? undefined,
    dividendYield: instrument.dividendYield ?? undefined,
    exchange: instrument.exchange ?? undefined,
    forwardPERatio: instrument.forwardPERatio ?? undefined,
    marketCap: toMarketCapNumber(instrument.marketCap),
    name: instrument.name?.trim() || symbol,
    pe: instrument.priceToEarnings ?? undefined,
    sector: instrument.gicsSectorName?.trim() || undefined,
    shareFloat: instrument.shareFloat ?? undefined,
    sharesOutstanding: instrument.sharesOutstanding ?? undefined,
    symbol,
  };
};

const normalizeSymbols = (symbols: string[]): string[] => [
  ...new Set(symbols.map(symbol => symbol.trim().toUpperCase()).filter(Boolean)),
];

const buildScreenFilters = (
  instrument: {
    gicsIndustryName?: string | null;
    gicsSectorName?: string | null;
    gicsSubIndustryName?: string | null;
  },
  useIndustry: boolean,
): string[] => {
  const subIndustry = instrument.gicsSubIndustryName?.trim();
  const industry = instrument.gicsIndustryName?.trim();
  const sector = instrument.gicsSectorName?.trim();
  const filters: string[] = [];

  if (useIndustry && subIndustry) {
    filters.push(`gicsSubIndustryName_eq_${subIndustry}`);
  }
  if (useIndustry && industry) {
    filters.push(`gicsIndustryName_eq_${industry}`);
  }
  if (sector) {
    filters.push(`gicsSectorName_eq_${sector}`);
  }

  return filters;
};

const getScannerManager = async (session: Session) => {
  const { ScannerManager } = await import('./manager');
  return session.getManager(ScannerManager);
};

export const getRelatedSymbolsForSymbol = async (
  session: Session,
  options: RelatedSymbolsForSymbolOptions,
): Promise<RelatedSymbol[]> => {
  const {
    excludeSymbols = [],
    fields = DEFAULT_SCANNER_FIELDS,
    limit = RELATED_SYMBOL_RESULT_LIMIT,
    symbol,
    useIndustry = true,
  } = options;

  const normalizedSymbol = symbol.trim().toUpperCase();
  if (!normalizedSymbol) return [];

  const scannerManager = await getScannerManager(session);
  const lookupFields = [...new Set([...DEFAULT_LOOKUP_FIELDS, ...fields])];
  const lookup = await scannerManager.getInstrumentsWithIQuery({
    fields: lookupFields,
    filtersAsString: `symbol_in_${normalizedSymbol}`,
    limit: 1,
    sortDir: ScannerProtos.SortDir.DESC,
    sortField: 'marketCap',
  });

  if (lookup.err || !lookup.ok?.instruments?.length) return [];

  const seedInstrument = lookup.ok.instruments[0];
  const screenFilters = buildScreenFilters(seedInstrument, useIndustry);
  if (screenFilters.length === 0) return [];

  let screenInstruments: NonNullable<typeof lookup.ok.instruments> = [];

  for (const screenFilter of screenFilters) {
    const screen = await scannerManager.getInstrumentsWithIQuery({
      fields: [...new Set([...DEFAULT_SCANNER_FIELDS, ...fields])],
      filtersAsString: screenFilter,
      limit: limit + excludeSymbols.length + 1,
      sortDir: ScannerProtos.SortDir.DESC,
      sortField: 'marketCap',
    });

    if (!screen.err && screen.ok?.instruments?.length) {
      screenInstruments = screen.ok.instruments;
      break;
    }
  }

  if (screenInstruments.length === 0) return [];

  const excluded = new Set([normalizedSymbol, ...excludeSymbols.map(value => value.trim().toUpperCase())]);
  const peersBySymbol = new Map<string, RelatedSymbol>();

  for (const instrument of screenInstruments) {
    const relatedSymbol = mapInstrumentToRelatedSymbol(instrument);
    if (!relatedSymbol.symbol || excluded.has(relatedSymbol.symbol) || peersBySymbol.has(relatedSymbol.symbol)) {
      continue;
    }
    peersBySymbol.set(relatedSymbol.symbol, relatedSymbol);
  }

  return [...peersBySymbol.values()].sort(byMarketCapDesc).slice(0, limit);
};

export const getRelatedSymbolsForSymbols = async (
  session: Session,
  options: RelatedSymbolsForSymbolsOptions,
): Promise<WatchlistRelatedSymbol[]> => {
  const {
    fields = DEFAULT_SCANNER_FIELDS,
    limit = RELATED_SYMBOL_RESULT_LIMIT,
    sectorLimit = RELATED_SYMBOL_SECTOR_LIMIT,
    seedLimit = RELATED_SYMBOL_SEED_LIMIT,
    symbols,
  } = options;

  const allSeedSymbols = normalizeSymbols(symbols);
  if (allSeedSymbols.length === 0) return [];

  const seedSymbols = sampleSymbolsEvenly(allSeedSymbols, seedLimit);
  const scannerManager = await getScannerManager(session);
  const excludedSymbols = new Set(allSeedSymbols);

  const lookup = await scannerManager.getInstrumentsWithIQuery({
    fields: ['symbol', 'gicsSectorName'],
    filtersAsString: `symbol_in_${seedSymbols.join(',')}`,
    limit: seedSymbols.length,
    sortDir: ScannerProtos.SortDir.DESC,
    sortField: 'marketCap',
  });
  if (lookup.err || !lookup.ok?.instruments?.length) return [];

  const sectors = pickTopSectors(lookup.ok.instruments, sectorLimit);
  if (sectors.length === 0) return [];

  const peerFetchLimit = getRelatedSymbolsFetchLimit(sectors.length, allSeedSymbols.length);
  const peersResponse = await scannerManager.getInstrumentsWithIQuery({
    fields: [...new Set([...DEFAULT_SCANNER_FIELDS, ...fields])],
    filtersAsString: `gicsSectorName_in_${sectors.join(',')}`,
    limit: peerFetchLimit,
    sortDir: ScannerProtos.SortDir.DESC,
    sortField: 'marketCap',
  });

  const peerInstruments = peersResponse.err || !peersResponse.ok?.instruments ? [] : peersResponse.ok.instruments;

  return mergeRelatedSymbols(peerInstruments, excludedSymbols, limit, sectors, mapInstrumentToWatchlistRelatedSymbol);
};
